import { useMemo, useState } from 'react';
import type { StagingCanonicalUploadBody } from '../api/admin';
import {
  classifyTable, LEVEL_HINT, LEVEL_LABEL, sortByNeed, TrackingLevel, trackingCsv,
} from '../modules/data-mapping/changeTracking';

const LEVEL_STYLE: Record<TrackingLevel, { bg: string; fg: string; border: string }> = {
  tracked:     { bg: '#e6f5ea', fg: '#1b7a3a', border: '#b9e0c4' },
  detectable:  { bg: '#eef2ff', fg: '#3b4ea1', border: '#d6deff' },
  'load-only': { bg: '#fff4e0', fg: '#8a5300', border: '#f1d29a' },
  none:        { bg: '#fde7e7', fg: '#a11414', border: '#f3c0c0' },
};
const LEVELS: TrackingLevel[] = ['none', 'load-only', 'detectable', 'tracked'];

// Which staging tables can tell what changed since the last push to
// ClientVerse, so a load sends only those rows instead of every row. Built from
// the schema in hand (a template or a client's own), by column name.
export default function ChangeTrackingReport({ schema, fileStem, onOpenTable }: {
  schema: StagingCanonicalUploadBody;
  fileStem: string;
  onOpenTable: (name: string) => void;
}) {
  const [only, setOnly] = useState<TrackingLevel | null>(null);
  const rows = useMemo(
    () => sortByNeed(schema.objects.map((o) => classifyTable(o.name, (o.fields ?? []).map((f) => f.name)))),
    [schema],
  );
  const counts = useMemo(() => {
    const c: Record<TrackingLevel, number> = { tracked: 0, detectable: 0, 'load-only': 0, none: 0 };
    for (const r of rows) c[r.level]++;
    return c;
  }, [rows]);
  const shown = only ? rows.filter((r) => r.level === only) : rows;

  const download = () => {
    const blob = new Blob([trackingCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileStem}-change-tracking.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="card" style={{ padding: 16 }}>
      <p style={{ marginTop: 0, fontSize: 13, color: '#444' }}>
        Every staging table should be able to tell what changed since the last push to ClientVerse, so a load
        sends only those rows. This is read from column names, so a table tracked some other way shows here
        until its schema carries the columns.
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'stretch', marginBottom: 12 }}>
        {LEVELS.map((l) => {
          const s = LEVEL_STYLE[l];
          const active = only === l;
          return (
            <button
              key={l}
              onClick={() => setOnly(active ? null : l)}
              aria-pressed={active}
              style={{
                textAlign: 'left', padding: '8px 12px', borderRadius: 6, cursor: 'pointer', minWidth: 170, maxWidth: 260,
                background: s.bg, color: s.fg, border: `${active ? 2 : 1}px solid ${active ? s.fg : s.border}`,
              }}
            >
              <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{counts[l]}</div>
              <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4 }}>{LEVEL_LABEL[l]}</div>
              <div style={{ fontSize: 11, marginTop: 2, color: '#555' }}>{LEVEL_HINT[l]}</div>
            </button>
          );
        })}
        <button onClick={download} style={{ ...btn, marginLeft: 'auto', alignSelf: 'flex-start' }}>Download CSV</button>
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid var(--mi-border)', borderRadius: 6 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
          <thead>
            <tr>
              {['Table', 'Tracking', 'Push record', 'Hash', 'Change markers', 'Load stamps', 'Write-back id', 'Delete / active flag'].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const s = LEVEL_STYLE[r.level];
              return (
                <tr key={r.table} style={{ borderTop: '1px solid var(--mi-border)' }}>
                  <td style={td}>
                    <button onClick={() => onOpenTable(r.table)} style={linkBtn} title="Open this table's fields">{r.table}</button>
                  </td>
                  <td style={td}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 4, whiteSpace: 'nowrap', background: s.bg, color: s.fg, border: `1px solid ${s.border}` }}>
                      {LEVEL_LABEL[r.level]}
                    </span>
                  </td>
                  <Cols names={r.pushRecord} />
                  <Cols names={r.hash} />
                  <Cols names={r.changeMarkers} />
                  <Cols names={r.loadStamps} />
                  <Cols names={r.writeBack} />
                  <Cols names={r.deletes} />
                </tr>
              );
            })}
            {shown.length === 0 && (
              <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: 'var(--mi-muted)' }}>No tables at this level.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Cols({ names }: { names: string[] }) {
  return (
    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>
      {names.length > 0 ? names.join(', ') : <span style={{ color: '#bbb', fontFamily: 'inherit' }}>—</span>}
    </td>
  );
}

const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4,
  color: 'var(--mi-muted)', background: '#fafbfc', whiteSpace: 'nowrap',
};
const td: React.CSSProperties = { padding: '6px 10px', verticalAlign: 'top' };
const btn: React.CSSProperties = {
  padding: '6px 12px', fontSize: 13, borderRadius: 4, cursor: 'pointer',
  background: '#fff', color: 'var(--mi-navy)', border: '1px solid var(--mi-border)',
};
const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
  fontFamily: 'monospace', fontWeight: 600, color: 'var(--mi-navy)', textAlign: 'left',
};
